import { CommonService } from 'app/services/common.service';
import { FormValidationService } from './../../../../services/form-validation.service';
import { DataService } from './../../../../services/data.service';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Identity } from '../../../../models';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

function validateBD(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    const birthDay = new Date(control.value);
    const minDate = new Date('1900-01-01');
    const maxDate = new Date();

    if (birthDay < minDate || birthDay >= maxDate) {
      return { BDInvalid: true }
    }

    return null;
  }
}

@Component({
  selector: 'app-student-identity-card',
  templateUrl: './student-identity-card.component.html',
  styleUrls: ['./student-identity-card.component.scss']
})

export class StudentIdentityCardComponent implements OnChanges, OnDestroy {

  @Input() identityInputData: Identity;
  @Output() identityUpdate: EventEmitter<any> = new EventEmitter<any>();
  ethnicityData = this.dataService.getEntity('Ethnicity').item || [];
  genderData = this.dataService.getEntity('Gender').item || [];
  gradeData = this.dataService.getEntity('Student Grade').item || [];
  timeData = this.dataService.getEntity('Graduation Month').item || [];
  fileArray;
  identityForm: FormGroup;
  identityModel = new Identity();
  testImage = '/assets/img/avatar.png';
  userYearArray = [];
  efc;
  miNameduplicate: any;
  backendUpdate = false;
  formsTouched = false;
  imageChange = false;
  private destroyed$ = new Subject();

  constructor(
    private formBuilder : FormBuilder,
    private dataService: DataService,
    private _formValidationService: FormValidationService,
    private _commonService: CommonService
  ) {
    this.buildForm();
    this.prepareUserYearArray();
    this._formValidationService.touchForms
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.identityForm.markAsTouched();
        (<any>Object).values(this.identityForm.controls).forEach((control: FormControl) => {
          control.markAsTouched();
        });
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['identityInputData'] !== undefined && changes['identityInputData'].currentValue !== undefined) {
    this.backendUpdate = true;
      this.hydrateForm(changes['identityInputData'].currentValue);
      this.backendUpdate = false;
      this.identityModel = changes['identityInputData'].currentValue;
    }
    if (this.identityForm && this.identityForm.dirty && this.identityForm.valid) {
      this.identityUpdate.emit(this.identityModel);
    }
  }

  prepareUserYearArray() {
    const currentYear = new Date().getFullYear();
    for (let i = 0; i < 5; i++) {
      this.userYearArray.push(currentYear + i)
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.formsTouched = false;
    this.imageChange = false;
  }
  buildForm() {
    this.identityForm = this.formBuilder.group({
      avatarUrlValue: '',
      firstName: ['', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(25), Validators.pattern(/^[a-zA-Z '-]+$/)])],
      miName: ['', Validators.compose([Validators.maxLength(1)])],
      lastName: ['', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(25), Validators.pattern(/^[a-zA-Z '-]+$/)])],
      genderValueId: ['0', Validators.required],
      userBirthday: ['', Validators.compose([Validators.required, validateBD()])],
      userMonthDate: ['0', Validators.required],
      userYearDate: '0',
      ethnicityId: ['0', Validators.required],
      gradeSchoolId: ['0', Validators.required],
      usCitizenValue: '',
      permanentResidentValue: '',
      efc: -1,
      familySize: [0, Validators.compose([Validators.required, Validators.min(0), Validators.max(99)])],
      deleteAvatar: false
    });
    this.identityForm.valueChanges
    .filter(() => this.identityForm.valid && !this.backendUpdate)
    .pipe(debounceTime(10), takeUntil(this.destroyed$))
    .subscribe(newIdentity => this.updateIdentity(newIdentity));

    this._formValidationService.registerForm('identityCard', this.identityForm);
  }

  hydrateForm(newData: Identity) {
    const values = {
      avatarUrlValue: newData.avatarURL !== ('' || undefined) ? newData.avatarURL : '/assets/img/avatar.png',
      firstName: newData.firstName,
      miName: newData.middleInitial,
      lastName: newData.lastName,
      genderValueId: newData.genderClassId ? newData.genderClassId : '0',
      userBirthday: this.transformBirthDate(newData.birthdate),
      userMonthDate: newData.graduationMonth ? newData.graduationMonth : '0',
      userYearDate: newData.graduationYear,
      ethnicityId: newData.raceClassId ? newData.raceClassId : '0',
      gradeSchoolId: newData.gradeClassId,
      usCitizenValue: (newData.citizenship && newData.citizenship.usCitizenId) ? newData.citizenship.usCitizenId.toString() : '',
      permanentResidentValue: (newData.citizenship && newData.citizenship.permanentResident) ? newData.citizenship.permanentResident.toString() : '',
      efc: (newData.efc === -1 || newData.efc === -2) ? '' : newData.efc,
      familySize: newData.familySize,
      deleteAvatar: false
    };
    this.identityForm.patchValue(values);
    this.testImage = values.avatarUrlValue || '/assets/img/avatar.png';
    this.efc = (newData.efc === -1 || newData.efc === -2) ? -1 : '';
  }

  getIdentityData() {
    if (this.identityForm && this.identityForm.valid) {
      this.identityForm.value.usCitizenValue = this.identityModel.citizenship.usCitizenId;
      this.identityForm.value.permanentResidentValue = this.identityModel.citizenship.permanentResident;
      this.identityForm.value.avatarUrlValue = this.identityForm.value.avatarUrlValue !== this.identityModel.avatarURL ? this.fileArray : null;
      this.identityForm.value.efc = this.identityForm.value.efc ? this.identityForm.value.efc : this.efc;
      return this.identityForm;
    } else {
      return false;
    }
  }

  fileChangeEvent(fileInput: any) {
    const reader = new FileReader();
    const readerByte = new FileReader();
    reader.readAsDataURL(fileInput.target.files[0]);
    reader.onload = () => this.identityModel.avatarURL = reader.result as any;
    readerByte.onload = () => this.fileArray = new Uint8Array(readerByte.result as any);
    readerByte.readAsArrayBuffer(fileInput.target.files[0]);
    this.imageChange = true;
  }

  efcKeyup() {
    this.efc = this.identityForm.value.efc > 0 ? 0 : this.identityForm.value.efc === 0 ? 0 : -1;
  }

  deleteStudentProfileImage() {
    this.identityModel.avatarURL = '';
    this.identityForm.value.deleteAvatar = true;
    this.imageChange = true;
  }

  unknowEfcChnage() {
    this.identityForm.patchValue({ efc: '' });
  }

  checkFamilySize(event) {
    return event.charCode >= 48 && event.charCode <= 57;
  }

  // consider using this method
  middleInitialValidation() {
    const result = this._commonService.checkMaxLength(this.identityForm.value.miName, 2);
    if (result) {
      this.miNameduplicate = this.identityForm.value.miName;
      this.identityForm.patchValue({ miName: this.identityForm.value.miName });
    } else {
      this.identityForm.patchValue({ miName: this.miNameduplicate });
    }
  }

  transformBirthDate(date: string): string | Date {
    date = date === '0001-01-01T00:00:00' ? '' : date;
    const dob = date ? new Date(date.split('T')[0]) : null;
    if (dob) {
      dob.setMinutes( dob.getMinutes() + dob.getTimezoneOffset() );
    }
    return dob
  }

  updateIdentity(values) {
    this.formsTouched = true;
  }

}
